import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';

import {
  buildLeafColumnIdIndexMap,
  leafIndicesForColumnRangeFromMap,
} from './tableColumnHoverUseCase';
import { getPinnedAwareVisibleLeafColumns } from './tableHeadUseCase';

/**
 * @typedef TableColumnHoverDispatchValue
 * @property {(indices: number[] | null) => void} setHoveredLeafIndices - Only TableBody should call this
 * @property {() => void} clearHoveredLeafIndices - Clears hover (e.g. mouse leaves scroll area)
 * @property {(columnId: string, colSpan: number) => number[]} leafIndicesForColumnRange
 */

/** Hover state: `undefined` = no provider; `null` = idle; `Set` = hovered column(s) */
/** @type {React.Context<Set<number> | null | undefined>} */
const TableColumnHoverStateContext = createContext(undefined);

/** @type {React.Context<TableColumnHoverDispatchValue | null>} */
const TableColumnHoverDispatchContext = createContext(null);

const emptyDispatch = Object.freeze({
  /** @type {(indices: number[] | null) => void} */
  setHoveredLeafIndices: () => {},
  clearHoveredLeafIndices: () => {},
  /** @type {(columnId: string, colSpan: number) => number[]} */
  leafIndicesForColumnRange: () => [],
});

/**
 * Provides column hover sync (header, group header stats, column stats footer) when body cells are hovered.
 * Dispatch (setters + leaf index helpers) is split from hover state so `TableBody` does not
 * re-render on every hover move when it only calls setters.
 * @param {object} props
 * @param {import('@tanstack/react-table').Table<any>} props.table
 * @param {React.ReactNode} props.children
 * @returns {JSX.Element}
 */
export function TableColumnHoverProvider({ table, children }) {
  const { columnVisibility, columnOrder, columnPinning } = table.getState();

  const leafIndexCache = useMemo(() => {
    const leaves = getPinnedAwareVisibleLeafColumns(table);
    const idToIndex = buildLeafColumnIdIndexMap(leaves);
    return {
      leafCount: leaves.length,
      idToIndex,
    };
  }, [table, columnVisibility, columnOrder, columnPinning]);

  const [hoveredLeafIndices, setHoveredLeafIndicesState] = useState(
    /** @type {Set<number> | null} */ (null),
  );

  const setHoveredLeafIndices = useCallback((/** @type {number[] | null | undefined} */ indices) => {
    if (indices === null || indices === undefined) {
      setHoveredLeafIndicesState(null);
      return;
    }
    setHoveredLeafIndicesState(new Set(indices));
  }, []);

  const clearHoveredLeafIndices = useCallback(() => {
    setHoveredLeafIndicesState(null);
  }, []);

  const leafIndicesForColumnRange = useCallback(
    (/** @type {string} */ columnId, /** @type {number} */ colSpan) => leafIndicesForColumnRangeFromMap(
      leafIndexCache.leafCount,
      leafIndexCache.idToIndex,
      columnId,
      colSpan,
    ),
    [leafIndexCache],
  );

  const dispatchValue = useMemo(
    () => ({
      setHoveredLeafIndices,
      clearHoveredLeafIndices,
      leafIndicesForColumnRange,
    }),
    [setHoveredLeafIndices, clearHoveredLeafIndices, leafIndicesForColumnRange],
  );

  return (
    <TableColumnHoverDispatchContext.Provider value={dispatchValue}>
      <TableColumnHoverStateContext.Provider value={hoveredLeafIndices}>
        {children}
      </TableColumnHoverStateContext.Provider>
    </TableColumnHoverDispatchContext.Provider>
  );
}

/**
 * Subscribe to hovered column leaf indices only (header, footers). Avoid in `TableBody`.
 * @returns {{ hoveredLeafIndices: Set<number> | null }}
 */
export function useTableColumnHoverStateOptional() {
  const hoveredLeafIndices = useContext(TableColumnHoverStateContext);
  if (hoveredLeafIndices === undefined) {
    return { hoveredLeafIndices: null };
  }
  return { hoveredLeafIndices };
}

/**
 * Stable dispatch + leaf index helpers (use in `TableBody`, scroll container clear). Does not
 * subscribe to hover state updates.
 * @returns {TableColumnHoverDispatchValue}
 */
export function useTableColumnHoverDispatchOptional() {
  const ctx = useContext(TableColumnHoverDispatchContext);
  return ctx ?? emptyDispatch;
}

/**
 * @typedef TableColumnHoverMergedValue
 * @property {Set<number> | null} hoveredLeafIndices
 * @property {(indices: number[] | null) => void} setHoveredLeafIndices
 * @property {() => void} clearHoveredLeafIndices
 * @property {(columnId: string, colSpan: number) => number[]} leafIndicesForColumnRange
 */

/**
 * @returns {TableColumnHoverMergedValue}
 */
export function useTableColumnHoverContext() {
  const hoveredLeafIndices = useContext(TableColumnHoverStateContext);
  const dispatch = useContext(TableColumnHoverDispatchContext);
  if (hoveredLeafIndices === undefined || !dispatch) {
    throw new Error('useTableColumnHoverContext must be used within TableColumnHoverProvider');
  }
  return {
    hoveredLeafIndices,
    ...dispatch,
  };
}
